/**
 * Betting controls (BETTING phase, players only). Bet must be ≥ $1 (MIN_BET_CENTS)
 * and ≤ this player's balance, in whole cents. The server validates everything;
 * this UI just keeps the input legal and sends `placeBet`. The croupier places
 * NO bet (GAME_SPEC §7) — the panel hides for them.
 */

import { useMemo, useState } from 'react';
import { MIN_BET_CENTS } from '@blackjack/shared';
import { useGameStore } from '../state/store';
import { useSend } from '../net/RealtimeProvider';
import { formatCents } from '../util/format';

export function BettingControls() {
  const send = useSend();
  const roster = useGameStore((s) => s.server.roster);
  const selfId = useGameStore((s) => s.serverAssignedId ?? s.identity?.clientId);
  const identity = useGameStore((s) => s.identity);

  const self = useMemo(() => roster.find((r) => r.id === selfId), [roster, selfId]);
  const balanceCents = self?.balanceCents ?? 0;
  const isCroupier = (self?.role ?? identity?.role) === 'croupier';

  // Bet is held in whole cents; UI shows dollars. Default to $1 or balance.
  const [betCents, setBetCents] = useState<number>(Math.min(MIN_BET_CENTS, balanceCents) || MIN_BET_CENTS);
  const [placed, setPlaced] = useState(false);

  if (isCroupier) {
    return (
      <div className="panel betting">
        <h3 className="panel__title heading-gold">You're the House</h3>
        <p className="betting__house">
          You play the dealer hand on a fixed strategy — no bet to place. Sit tight
          while the players wager, then the cards come out.
        </p>
      </div>
    );
  }

  const clamp = (c: number) => Math.max(MIN_BET_CENTS, Math.min(Math.round(c), balanceCents));
  const valid = betCents >= MIN_BET_CENTS && betCents <= balanceCents;

  const place = () => {
    if (!valid) return;
    send({ type: 'placeBet', amountCents: betCents });
    setPlaced(true);
  };

  const presets = [MIN_BET_CENTS, 500, 1000, balanceCents].filter((c, i, a) => a.indexOf(c) === i);

  return (
    <div className="panel betting">
      <h3 className="panel__title heading-gold">Your Bet</h3>
      <p className="betting__balance">
        Balance <span className="numeric">{formatCents(balanceCents)}</span>
      </p>

      <div className="betting__amount numeric">{formatCents(betCents)}</div>

      <input
        type="range"
        min={MIN_BET_CENTS}
        max={Math.max(MIN_BET_CENTS, balanceCents)}
        step={1}
        value={betCents}
        onChange={(e) => setBetCents(clamp(Number(e.target.value)))}
        disabled={placed || balanceCents < MIN_BET_CENTS}
      />

      <div className="betting__presets">
        {presets.map((c) => (
          <button
            key={c}
            type="button"
            className="chip-btn"
            disabled={placed || c > balanceCents}
            onClick={() => setBetCents(clamp(c))}
          >
            {formatCents(c)}
          </button>
        ))}
      </div>

      <button type="button" className="btn-gold" disabled={!valid || placed} onClick={place}>
        {placed ? 'Bet Placed' : 'Place Bet'}
      </button>
      {balanceCents < MIN_BET_CENTS && (
        <p className="betting__warn">Not enough balance to bet this round.</p>
      )}
    </div>
  );
}
